/**
 * The account page. The identity header is the desk's one bold element: store name large, the
 * entity, processor, MID and status beside it, vital signs in a row underneath. Below, the chain,
 * the people, the details and what changed recently. Data comes from one round trip and is
 * usually already in the cache from the lookup's prefetch.
 */
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { type AccountDetail, type AccountEvent, type AccountTicketRow } from '@lsc/contract';
import { Communication } from '../../components/account/communication';
import { Locations } from '../../components/account/locations';
import { People } from '../../components/account/people';
import { Vitals } from '../../components/account/vitals';
import { ticketAgeHours } from '@lsc/domain';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Section } from '../../components/ui/section';
import { StatusWord, TicketStatusWord, statusText } from '../../components/ui/status';
import { useProvideAssistantContext } from '../../lib/assistant-context';
import { age, day, processorName, whereIs } from '../../lib/format';
import { openCapture } from '../../components/capture/capture-store';
import { accountQuery } from '../../lib/queries';
import { describeRpcError } from '../../lib/rpc';

export const Route = createFileRoute('/accounts/$accountId')({
  loader: ({ context, params }) => context.queryClient.query({ ...accountQuery(params.accountId), staleTime: 'static' }),
  component: AccountPage,
  pendingComponent: () => <div className="px-6 pt-6 text-body text-ink-3">Opening account</div>,
  errorComponent: ({ error }) => <div className="px-6 pt-6 text-body text-alert">{describeRpcError(error)}</div>,
});

function AccountPage() {
  const { accountId } = Route.useParams();
  const { data } = useQuery(accountQuery(accountId));
  useProvideAssistantContext(data ? [{ label: data.label }, { label: `${processorName(data.processor)} ${data.mid ?? ''}`.trim() }] : null);

  if (data === null) {
    return (
      <div className="px-6 pt-6">
        <p className="text-body text-ink-2">That account is not in the book. It may have been merged or the link is stale.</p>
        <Link to="/" className="mt-2 inline-block text-body text-info hover:underline">
          Back to look up
        </Link>
      </div>
    );
  }
  if (!data) return null;
  return <Account a={data} />;
}

function Account({ a }: { a: AccountDetail }) {
  const logTicket = () => {
    openCapture({ accountId: a.accountId });
  };
  return (
    <div className="mx-auto w-full max-w-[1120px] px-6 pt-4 pb-12">
      <Link to="/" className="inline-flex items-center gap-1.5 text-hint text-ink-3 hover:text-ink">
        <ArrowLeft className="size-3.5" /> Look up
      </Link>

      <header className="mt-3">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <h1 className="text-name font-semibold text-ink">{a.label}</h1>
          <StatusWord status={a.status} className="text-emphasis" />
          {a.env !== 'production' && <span className="text-hint text-attention">{a.env}</span>}
          <Button variant="primary" className="ml-auto self-center" onClick={logTicket} title="Log a ticket (C)">
            Log a ticket <kbd className="bg-canvas/20 text-canvas">C</kbd>
          </Button>
        </div>
        <p className="mt-1 flex flex-wrap items-center gap-x-3 text-body text-ink-2">
          {a.legalName && a.legalName !== a.label && <span>{a.legalName}</span>}
          <span>{processorName(a.processor)}</span>
          {a.mid ? (
            <span className="mono text-ink">{a.mid}</span>
          ) : (
            <span className="text-ink-3">MID pending</span>
          )}
          {a.address?.city && (
            <span>
              {a.address.city}
              {a.address.zip ? ` ${a.address.zip}` : ''}
            </span>
          )}
        </p>
      </header>

      <Vitals a={a} />

      {/* Tickets lead in a single column (the point of the page on a call) and head the right column when there are two. */}
      <div className="mt-6 grid gap-6 @[860px]/main:grid-cols-[minmax(0,1fr)_360px] @[860px]/main:grid-rows-[auto_1fr]">
        <div className="min-w-0 @[860px]/main:col-start-2 @[860px]/main:row-start-1">
          <Tickets t={a.tickets} />
        </div>
        <div className="flex min-w-0 flex-col gap-6 @[860px]/main:col-start-1 @[860px]/main:row-start-1 @[860px]/main:row-span-2">
          <Locations a={a} />
          <Communication accountId={a.accountId} locations={a.locations.length} />
          <Events events={a.events} />
        </div>
        <div className="flex min-w-0 flex-col gap-6 @[860px]/main:col-start-2 @[860px]/main:row-start-2">
          <People people={a.people} accountId={a.accountId} />
        </div>
      </div>
    </div>
  );
}



function Tickets({ t }: { t: AccountDetail['tickets'] }) {
  const none = t.openCount === 0 && t.closedCount === 0;
  return (
    <Section title="Tickets" aside={none ? undefined : `${t.openCount} open, ${t.closedCount} resolved`}>
      {none ? (
        <p className="px-4 py-3 text-body text-ink-3">No tickets yet. The first call logs one.</p>
      ) : (
        <>
          {t.open.length > 0 && (
            <ul>
              {t.open.map((r) => (
                <TicketRow key={r.ticketId} r={r} />
              ))}
            </ul>
          )}
          {t.recentClosed.length > 0 && (
            <details className={`group ${t.open.length > 0 ? 'border-t border-line' : ''}`} open={t.open.length === 0}>
              <summary className="cursor-pointer list-none px-4 py-2 text-hint text-ink-3 hover:text-ink">
                Recent resolved ({t.closedCount}){t.closedCount > t.recentClosed.length ? `, last ${t.recentClosed.length}` : ''}
              </summary>
              <ul className="border-t border-line">
                {t.recentClosed.map((r) => (
                  <li key={r.ticketId} className="border-t border-line px-4 py-2 first:border-t-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <Link to="/tickets/$ticketId" params={{ ticketId: r.ticketId }} className="min-w-0 flex-1 truncate text-body text-ink hover:underline" preload="intent">
                        {r.subject}
                      </Link>
                      <span className="shrink-0 text-hint text-ink-3">{day(r.closedAt)}</span>
                    </div>
                    {r.resolution && <p className="mt-0.5 text-hint text-ink-2">{r.resolution.length > 140 ? `${r.resolution.slice(0, 140)}...` : r.resolution}</p>}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </Section>
  );
}

function TicketRow({ r }: { r: AccountTicketRow }) {
  const where = whereIs(r.routedTo, r.waitingOn);
  return (
    <li className="border-t border-line px-4 py-2 first:border-t-0">
      <Link to="/tickets/$ticketId" params={{ ticketId: r.ticketId }} className="block truncate text-body font-medium text-ink hover:underline" preload="intent">
        {r.subject}
      </Link>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-hint text-ink-3">
        <TicketStatusWord status={r.status} />
        <span>{age(ticketAgeHours(r.createdAt))}</span>
        {where && <span>{where}</span>}
        {r.loggedByName && <span>by {r.loggedByName}</span>}
        <span className="mono ml-auto">{r.ticketNumber}</span>
      </div>
    </li>
  );
}



function str(v: unknown): string {
  return typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
}

function describeEvent(e: AccountEvent): string {
  const b = (e.before ?? {}) as Record<string, unknown>;
  const a = (e.after ?? {}) as Record<string, unknown>;
  switch (e.kind) {
    case 'status_changed':
      return `Status ${statusTextSafe(str(b['status']))} to ${statusTextSafe(str(a['status']))}`;
    case 'closed':
      return 'Account closed at the processor';
    case 'reopened':
      return 'Account reopened at the processor';
    case 'name_drift':
      return `Name changed from "${str(b['dbaName'])}" to "${str(a['dbaName'])}"`;
    case 'risk_level_changed':
      return `Risk level ${str(b['riskLevel']) || 'none'} to ${str(a['riskLevel']) || 'none'}`;
    case 'batch_resumed':
      return `Batching resumed, last batch ${day(str(a['lastBatchOn'])) ?? ''}`;
    case 'contact_changed':
      return 'Customer service contact changed at the processor';
    case 'mid_assigned':
      return `MID assigned: ${str(a['epiMid']) || str(a['mid'])}`;
    case 'mid_shape_mismatch':
      return 'MID does not fit the processor pattern';
    case 'mid_collision':
      return 'Another account already carries this MID';
    default:
      return e.kind.replaceAll('_', ' ');
  }
}

function statusTextSafe(v: string): string {
  try {
    return statusText(v as Parameters<typeof statusText>[0]);
  } catch {
    return v;
  }
}

function Events({ events }: { events: AccountEvent[] }) {
  return (
    <Section title="Recent changes" aside="from the processor sweeps">
      {events.length === 0 ? (
        <p className="px-4 py-3 text-body text-ink-3">Nothing has changed since this account was first seen.</p>
      ) : (
        <ul>
          {events.map((e) => (
            <li key={e.id} className="flex items-start gap-3 border-t border-line px-4 py-2 first:border-t-0">
              <span aria-hidden className={`mt-2 size-1.5 shrink-0 rounded-full ${e.severity === 'high' ? 'bg-alert' : e.severity === 'warn' ? 'bg-attention' : 'bg-ink-3'}`} />
              <span className="flex-1 text-body text-ink">{describeEvent(e)}</span>
              <span className="whitespace-nowrap text-hint text-ink-3">{day(e.occurredAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
